import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { getHorizontalScrollRegion } from "./horizontal-scroll-region";
export type HorizontalScrollRegionSelectors = {
  start: string;
  end: string;
  container: string;
};
export function useHorizontalScrollRegion(viewportRef, proxyRef, selectors) {
  const $ = _c(13);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...getHorizontalScrollRegion(0, 0, 0, 0),
      clipHeight: 0,
      paddingBottom: 0
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [region, setRegion] = React.useState(t0);
  let t1;
  let t2;
  if ($[1] !== proxyRef || $[2] !== selectors || $[3] !== viewportRef) {
    t1 = () => {
      const viewport = viewportRef.current;
      const proxy = proxyRef.current;
      if (!selectors || !viewport || !proxy) {
        return;
      }
      const syncProxy = () => {
        if (proxy.scrollLeft !== viewport.scrollLeft) {
          proxy.scrollLeft = viewport.scrollLeft;
        }
      };
      const syncViewport = () => {
        if (viewport.scrollLeft !== proxy.scrollLeft) {
          viewport.scrollLeft = proxy.scrollLeft;
        }
      };
      const measure = () => {
        const viewportLeft = viewport.getBoundingClientRect().left + viewport.clientLeft;
        const viewportRight = viewportLeft + viewport.clientWidth;
        const startCells = [...viewport.querySelectorAll(selectors.start)];
        const endCells = [...viewport.querySelectorAll(selectors.end)];
        const header = viewport.querySelector(selectors.container);
        const gap = header?.parentElement ? Number.parseFloat(getComputedStyle(header.parentElement).columnGap) || 0 : 0;
        const start = startCells.reduce((inset, cell) => Math.max(inset, cell.getBoundingClientRect().right - viewportLeft + gap), 0);
        const end = endCells.reduce((inset_0, cell_0) => Math.max(inset_0, viewportRight - cell_0.getBoundingClientRect().left + gap), 0);
        const geometry = getHorizontalScrollRegion(viewport.clientWidth, viewport.scrollWidth, start, end);
        const nativeHeight = viewport.offsetHeight - viewport.clientHeight;
        const scrollbarSize = Number.parseFloat(getComputedStyle(viewport).getPropertyValue("--scrollbar-size")) || nativeHeight;
        const clipHeight = geometry.maxScrollLeft > 0 ? Math.max(nativeHeight, scrollbarSize) : 0;
        const next = {
          ...geometry,
          clipHeight,
          paddingBottom: Math.max(0, clipHeight - nativeHeight)
        };
        setRegion(current => current.start === next.start && current.end === next.end && current.trackWidth === next.trackWidth && current.contentWidth === next.contentWidth && current.maxScrollLeft === next.maxScrollLeft && current.clipHeight === next.clipHeight && current.paddingBottom === next.paddingBottom ? current : next);
      };
      const resizeObserver = new ResizeObserver(measure);
      let mutationObserver;
      const observeContent = () => {
        resizeObserver.disconnect();
        resizeObserver.observe(viewport);
        if (viewport.firstElementChild) {
          resizeObserver.observe(viewport.firstElementChild);
        }
        const cells = viewport.querySelectorAll(`${selectors.start}, ${selectors.end}`);
        cells.forEach(cell_1 => resizeObserver.observe(cell_1));
        const header_0 = viewport.querySelector(selectors.container);
        if (header_0) {
          resizeObserver.observe(header_0);
          mutationObserver?.disconnect();
          mutationObserver?.observe(header_0, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["style", "data-sticky-column"]
          });
        }
        measure();
      };
      mutationObserver = new MutationObserver(observeContent);
      observeContent();
      viewport.addEventListener("scroll", syncProxy, {
        passive: true
      });
      proxy.addEventListener("scroll", syncViewport, {
        passive: true
      });
      return () => {
        resizeObserver.disconnect();
        mutationObserver?.disconnect();
        viewport.removeEventListener("scroll", syncProxy);
        proxy.removeEventListener("scroll", syncViewport);
      };
    };
    t2 = [viewportRef, proxyRef, selectors];
    $[1] = proxyRef;
    $[2] = selectors;
    $[3] = viewportRef;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  React.useLayoutEffect(t1, t2);
  let t3;
  if ($[6] !== proxyRef || $[7] !== viewportRef) {
    t3 = () => {
      if (viewportRef.current && proxyRef.current && proxyRef.current.scrollLeft !== viewportRef.current.scrollLeft) {
        proxyRef.current.scrollLeft = viewportRef.current.scrollLeft;
      }
    };
    $[6] = proxyRef;
    $[7] = viewportRef;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== proxyRef || $[10] !== region || $[11] !== viewportRef) {
    t4 = [region, viewportRef, proxyRef];
    $[9] = proxyRef;
    $[10] = region;
    $[11] = viewportRef;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  React.useLayoutEffect(t3, t4);
  return region;
}